/**
 * @file index.tsx
 *
 * @fileoverview Renders blog page.
 */

import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import Layout from '~/jobiqo/components/Layout';
import { BlogPost } from '~/jobiqo/models/blog';
import { ContentResponse } from '~/jobiqo/types/types';
import AdUnitBlogSidebarSlot from '~/jobiqo/components/Slots/AdUnitBlogSidebarSlot';
import AdUnitBlogRowSlot from '~/jobiqo/components/Slots/AdUnitBlogRowSlot';
import SocialMediaSharingButtons from '~/jobiqo/components/SocialMedia/SharingButtons';
import dynamic from 'next/dynamic';
import BlogBreadcrumbs from '~/jobiqo/components/Blog/components/Breadcrumbs';
import { BlogJobTeasersRowProps } from '~/jobiqo/components/Blog/Rows/JobTeasers';
import BlogTags from '~/jobiqo/components/Blog/components/Tags';
import BlogRealtedBlogs, {
  BlogRealtedBlogsProps,
} from '~/jobiqo/components/Blog/components/RelatedBlogs';
import { ReactNode } from 'react';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { OneSidebarLayout } from '~/vcl/templates/01-layout/02-grid-layouts/02-one-sidebar';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Box } from '~/vcl/atoms/07-box';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Badge } from '~/vcl/atoms/03-badge';
import { Text } from '~/vcl/atoms/13-text/03-text';
import styles from './blog.module.scss';
import BlogBanner from '~/jobiqo/components/Blog/components/Banner';
import { BlogArticlePlaceholder } from '~/vcl/organisms/05-placeholders/08-blog/02-article';
import { parseUniversalDate } from '~/jobiqo/utils/dates';
import { JsonLd } from 'react-schemaorg';
import { BlogPosting } from 'schema-dts';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const BlogCTARow = dynamic(() => import('~/jobiqo/components/Blog/Rows/CTA'));
const BlogJobTeasersRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/JobTeasers'),
);
const BlogMetatags = dynamic(
  () => import('~/jobiqo/components/Blog/components/Metatags'),
);
const BlogQuoteRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/Quote'),
);
const BlogImageRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/Image'),
);
const BlogSimpleTextRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/SimpleText'),
);
const BlogTextImageRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/TextImage'),
);
const BlogOrganizationProfileRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/OrganizationProfile'),
);
const BlogInsecureScriptRow = dynamic(
  () => import('~/jobiqo/components/Blog/Rows/BlogInsecureScript'),
);

export interface BlogProps extends BlogRealtedBlogsProps {
  /**
   * The query data.
   */
  data?: ContentResponse;
  /**
   * The error flag.
   */
  error: boolean;
  /**
   * The loading flag.
   */
  loading: boolean;
}

// Removes blog teaser row that doesn't have any matched jobs.
function removeEmptyBlogTeasersRowFilter(row: any) {
  return row.bundle === 'blog_job_teasers'
    ? (row as BlogJobTeasersRowProps).jobs.length > 0
    : row;
}

export default function Blog({
  data,
  loading,
  error,
  relatedBlogs,
}: BlogProps) {
  const intl = useIntl();
  const frontendConfig = useFrontendConfig();
  const SOCIAL_ICONS_COLORED = frontendConfig.APP.SOCIAL_ICONS_COLORED;
  let ErrorList: ReactNode = null;
  const frontendTheme = getFrontendTheme(frontendConfig);

  if (error) {
    ErrorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="blog_error"
          defaultMessage="There was an error receiving blog data."
        />
      </MessageAlert>
    );
  }

  if (data?.errors && data.errors.length > 0) {
    ErrorList = <ContentErrorList errors={data?.errors} />;
  }

  const blog = data?.content as BlogPost;

  const blogImageAlt = blog.image?.alt ?? blog.title;
  const blogImageTitle = blog.image?.title ?? blog.title;

  // The only thing right now in the sidebar are ads. if the site has no ads
  // We should render a smaller centered layout.
  const sidebarAds = <AdUnitBlogSidebarSlot />;
  const emptySidebar = sidebarAds.type() === null;

  return (
    <>
      <BlogMetatags metatags={blog.metatags ?? []} />
      <JsonLd<BlogPosting>
        item={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: blog.title,
          datePublished: new Date(blog.date).toISOString(),
          ...(blog?.modifiedDate && {
            dateModified: new Date(blog?.modifiedDate).toISOString(),
          }),
          ...(blog?.image?.url && {
            image: [blog.image.url],
          }),
          ...(blog.author && {
            author: [
              {
                '@type': 'Person',
                name: blog.author,
              },
            ],
          }),
        }}
      />
      <Layout fullWidthContainer={true}>
        {blog.image?.url && (
          <BlogBanner
            image={{
              ...blog.image,
              alt: blogImageAlt,
              title: blogImageTitle,
            }}
          />
        )}
        <Box
          boxShadow="none"
          padding={0}
          margin={0}
          backgroundColor={frontendTheme.BLOG.DETAIL.BG}
          borderRadius="0px"
        >
          <Main size="large">
            <OneSidebarLayout
              sidebarPosition="right"
              sidebar={
                emptySidebar ? null : (
                  <Spacing marginTop={8}>{sidebarAds}</Spacing>
                )
              }
            >
              <Main
                {...(emptySidebar
                  ? { size: frontendTheme.BLOG.DETAIL.CONTAINER }
                  : null)}
              >
                <Box
                  {...(blog.image?.url && { className: styles.box })}
                  marginBottom={11}
                >
                  {loading ? (
                    <BlogArticlePlaceholder />
                  ) : (
                    <>
                      {ErrorList}
                      <FlexItems justifyContent="space-between">
                        <FlexItem>
                          <BlogBreadcrumbs blog={blog} />
                        </FlexItem>
                        {blog.featured && (
                          <FlexItem>
                            <Badge badgeStyle="featured">
                              {intl.formatMessage({
                                id: 'advertorial_label',
                                defaultMessage: 'Advertorial',
                              })}
                            </Badge>
                          </FlexItem>
                        )}
                      </FlexItems>
                      <Spacing size="medium" />
                      <Text
                        marginTop={0}
                        color="gray7"
                        fontSize={1}
                        textTransform="uppercase"
                      >
                        <FormattedDate
                          dateStyle="medium"
                          value={parseUniversalDate(blog.date)}
                        />
                        {blog.author && (
                          <>
                            {' '.repeat(3)}●{' '.repeat(3)}
                            {blog.author}
                          </>
                        )}
                      </Text>
                      <Spacing />
                      <h1 data-test-id="blog-h1">{blog.title}</h1>
                      <Spacing size="large" />
                      {blog.blogPostRows
                        ?.filter(removeEmptyBlogTeasersRowFilter)
                        .map((row: any, index) => (
                          <Spacing paddingBottom={8} key={index}>
                            {(() => {
                              switch (row?.bundle) {
                                case 'blog_quote':
                                  return (
                                    <BlogQuoteRow
                                      key={index}
                                      text={row.quote}
                                      author={row.author}
                                      image={row.image}
                                    />
                                  );
                                case 'blog_simple_text':
                                  return (
                                    <BlogSimpleTextRow
                                      key={index}
                                      text={row.text}
                                    />
                                  );
                                case 'blog_text_image':
                                  return (
                                    <BlogTextImageRow
                                      key={index}
                                      align={row.align}
                                      text={row.text}
                                      image={row.image}
                                    />
                                  );
                                case 'blog_image':
                                  return <BlogImageRow image={row.image} />;
                                case 'blog_ad_unit':
                                  return <AdUnitBlogRowSlot row={row} />;
                                case 'blog_job_teasers':
                                  return (
                                    <BlogJobTeasersRow
                                      jobs={row.jobs}
                                      url={row.url}
                                    />
                                  );
                                case 'blog_cta':
                                  return (
                                    <BlogCTARow
                                      button={row.button}
                                      description={row.description}
                                      position={row.position}
                                    />
                                  );
                                case 'blog_insecure_script':
                                  return (
                                    <BlogInsecureScriptRow
                                      bundle={row.bundle}
                                      scriptContent={row.scriptContent}
                                      index={index}
                                    />
                                  );
                                case 'organization_profile':
                                  return (
                                    <BlogOrganizationProfileRow {...row} />
                                  );
                                default:
                                  return null;
                              }
                            })()}
                          </Spacing>
                        ))}
                      <BlogTags tags={blog.tags} />
                      <Spacing size="medium" />
                      <SocialMediaSharingButtons
                        url={blog.url.path}
                        title={intl.formatMessage(
                          {
                            id: 'blog_share',
                            defaultMessage: 'Share blog {title}',
                          },
                          { title: blog.title },
                        )}
                        coloredIcons={SOCIAL_ICONS_COLORED}
                        sharingEntity={blog}
                        sharingEntityType={'blog'}
                      />
                    </>
                  )}
                </Box>
              </Main>
            </OneSidebarLayout>
            <Spacing size="medium" />
            <BlogRealtedBlogs loading={loading} relatedBlogs={relatedBlogs} />
          </Main>
        </Box>
      </Layout>
    </>
  );
}

export { Blog };
